import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import {bzDayToDate, type BzDay} from '@bringoz/ui';
import * as moment from 'moment';

require('./bringoz-date-picker.scss');
/**
 * The console's date field, drawn by `bz-date-picker` from @bringoz/ui.
 *
 * Keeps the API its callers already use - a timestamp in, a timestamp out - and converts at this
 * boundary, because the library speaks calendar days ('YYYY-MM-DD') only:
 * - in: the timestamp's day in the browser's local time, as `splitService.getNewDateFormat` gives it;
 * - out: local midnight of the picked day.
 * Both are what the ng-bootstrap version did, so no caller sees a different value.
 */
@Component({
  standalone: false,
  selector: 'app-bringoz-date-picker',
  templateUrl: './bringoz-date-picker.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class BringozDatePickerComponent implements OnChanges {
  @Input() date: number;
  @Input() configTimeZone: string;
  @Input() placeholder: string = 'Enter date';
  @Input() minDate: number;
  @Input() maxDate: number;
  @Input() isRequired: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() isHideLabel: boolean = false;
  @Input() isAllowClear: boolean = false;
  @Input() onlyValidPredicate: (date: Date) => boolean;
  @Output() onChange = new EventEmitter<number>();

  day: BzDay | null = null;
  minDay: BzDay | null = null;
  maxDay: BzDay | null = null;
  dayFilter: ((day: BzDay) => boolean) | null = null;
  dateFormat: string;
  isTouched = false;

  constructor(@Inject('splitService') private splitService) {
    this.dateFormat = this.splitService.getDateAndTimeConfig().fullDate || 'MM/DD/YYYY';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.date || changes.configTimeZone) {
      this.day = this.toDay(this.date);
    }
    if (changes.minDate || changes.maxDate || changes.configTimeZone) {
      this.minDay = this.toDay(this.minDate);
      this.maxDay = this.toDay(this.maxDate);
    }
    if (changes.onlyValidPredicate) {
      const predicate = this.onlyValidPredicate;
      this.dayFilter = predicate ? (day: BzDay) => predicate(bzDayToDate(day)) : null;
    }
  }

  onDayChange(day: BzDay | null): void {
    this.day = day;
    this.isTouched = true;
    this.onChange.emit(day ? bzDayToDate(day).getTime() : undefined);
  }

  private toDay(timestamp: number): BzDay | null {
    return timestamp ? moment(this.splitService.getNewDateFormat(timestamp)).format('YYYY-MM-DD') : null;
  }
}
